import { Component, EventEmitter, Inject, Input, OnInit, Output, OnDestroy } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GenericResponse, Select } from '@interfaces';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';
import { UtilService } from 'src/app/services/util.service';
import { NewContractLargeAccounts, NewOffer, SelectedCampaign } from '../../new-contract-large-accounts.model';
import { NewContractLargeAccountsService } from '../../new-contract-large-accounts.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-new-contract-data-offer',
  templateUrl: './new-contract-data-offer.component.html',
  styleUrls: ['./new-contract-data-offer.component.scss']
})
export class NewContractDataOfferComponent implements OnInit, OnDestroy {
  private numRegex = /^(\d{1,2}(\,\d{1,3})?|\,?\d{1,2})$/;
  public dataOfferForm: FormGroup = new FormGroup({
    campaign: new FormControl(null, Validators.required),
    level: new FormControl({ value: null, disabled: true }, Validators.required),
    renovate: new FormControl(null, Validators.required),
    electric: new FormControl(null, Validators.required),
    omip: new FormControl(null, [Validators.required, Validators.maxLength(6), Validators.pattern(this.numRegex)])
  });
  public campaign = [];
  public campaignList: Select[] = [];
  public levelList: Select[] = [];
  public approved: boolean;
  public callQuery: boolean;
  private destroyed$: Subject<boolean> = new Subject<boolean>();

  @Input() dataOffer: NewOffer;
  @Input() dataForms: NewContractLargeAccounts;
  @Output() canGoNext: EventEmitter<SelectedCampaign> = new EventEmitter();
  @Output() changeLoader: EventEmitter<boolean> = new EventEmitter();

  constructor(
    private newContractLargeAccountsService: NewContractLargeAccountsService,
    private utilService: UtilService,
    private snackBar: MatSnackBar,
    @Inject(I18NEXT_SERVICE) private i18NextService: ITranslationService
  ) { }

  ngOnInit(): void {
    this.patchValuesForm();
    this.dataOfferForm.get('omip').setValidators(this.validatorMinValue(0));
    this.getCampaigns();
    this.subscribeControls();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private patchValuesForm(): void {
    if (this.dataOffer.codigoCampania && this.dataForms.valorOMIP) {
      this.dataOfferForm.patchValue({
        campaign: this.dataOffer.codigoCampania,
        level: this.dataOffer.nombreNivel,
        renovate: this.dataOffer.renovacionTacita,
        electric: this.dataOffer.impuestoElectrico,
        omip: this.dataForms.valorOMIP
      });
      this.dataOfferForm.get('omip').disable();
      this.dataOfferForm.get('level').enable();
      this.callQuery = true;
      this.approved = true;
    }
  }

  private validatorMinValue = (min: number): ValidatorFn => {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return { min_value_error: true };
      }
      return parseFloat(value.toString().replace(',', '.')) <= min ? { min_value_error: true } : null;
    };
  }

  private getCampaigns(): void {
    this.changeLoader.emit(true);
    const queryParams = {
      CodInstalaciones: this.dataOffer.codesInstalacion,
      CodRazonesSociales: this.dataOffer.codesRazonSocial,
      CodCuentaNegociadora: this.dataOffer.codCuentaNegociadora,
    };
    this.newContractLargeAccountsService.getCampaigns(queryParams).pipe(takeUntil(this.destroyed$)).subscribe(
      (response: GenericResponse) => {
        if (response.success) {
          this.campaign = response.data;
          this.campaignList = response.data.map(value => ({ value: value.codCampania, label: value.nombreCampania }));
          if (this.dataOfferForm.get('level').value) {
            this.levelList = this.campaign.find(value =>
              value.codCampania === this.dataOfferForm.get('campaign').value
            ).niveles.map(value => ({ value: value.nombreNivel, label: value.nombreNivel }));
            this.goNextEmit(true);
          }
        } else {
          this.utilService.toastErrors(response);
        }
        this.changeLoader.emit(false);
      },
      error => {
        this.changeLoader.emit(false);
        this.utilService.toastErrors(error);
      }
    );
  }

  private subscribeControls(): void {
    this.dataOfferForm.get('campaign').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(res => {
      this.levelList = this.campaign.find(value =>
        value.codCampania === res
      ).niveles.map(value => ({ value: value.nombreNivel, label: value.nombreNivel }));
      this.dataOfferForm.get('level').setValue(null);
      this.dataOfferForm.get('level').enable();
    });
    this.dataOfferForm.get('omip').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.callQuery = false;
      this.approved = false;
    });
    this.dataOfferForm.statusChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
      this.goNextEmit(value === 'VALID' && this.approved);
    });
  }

  public handleQuery(): void {
    this.changeLoader.emit(true);
    this.newContractLargeAccountsService.putQueryOffer(
      `?CodCuentaNegociadora=${this.dataOffer.codCuentaNegociadora}&CodigosCRMRazones=${this.dataOffer.codesRazonSocial}&OMIP=${parseFloat(this.dataOfferForm.get('omip').value)}`
    ).pipe(takeUntil(this.destroyed$)).subscribe(
      (response: GenericResponse) => {
        if (response.success) {
          this.callQuery = true;
          const notApproved = response.data.filter(value => value.codRespuesta !== 'ACEPTADO');
          if (notApproved.length === 0) {
            this.approved = true;
          } else {
            this.approved = false;
            let message = '';
            notApproved.forEach(element => {
              message += this.i18NextService.t('NEW_CONTRACT_LA.BUSINESS') + ' : ' + element.nombreazonSocial + ' , ' +
                this.i18NextService.t('NEW_CONTRACT_LA.ERROR') + ' : ' + element.descripcionRespuesta + ' ';
            });
            this.snackBar.open(message, 'x', { duration: 10000, panelClass: 'bg-error' });
          }
        } else {
          this.callQuery = false;
          this.approved = false;
          this.utilService.toastErrors(response);
        }
        this.goNextEmit(this.approved && this.dataOfferForm.valid);
        this.changeLoader.emit(false);
      },
      error => {
        this.callQuery = false;
        this.approved = false;
        this.goNextEmit(false);
        this.changeLoader.emit(false);
        this.utilService.toastErrors(error);
      }
    );
  }

  private goNextEmit(next: boolean): void {
    const data: SelectedCampaign = {
      ...{
        valid: next,
        campaingLabel: this.campaignList?.find(res => this.dataOfferForm.get('campaign')?.value === res?.value)?.label,
      },
      ...this.dataOfferForm.getRawValue()
    };
    this.canGoNext.emit(data);
  }
}
